import React from 'react';
import { useTenant } from '../context/TenantContext';

/**
 * TenantLoadingScreen Component
 *
 * Displays loading and error states for tenant/gallery configuration loading.
 * Returns null when not loading and no error exists (allowing main app to render).
 *
 * Features:
 * - Animated loading spinner with "Loading gallery..." message
 * - Error screen with retry functionality
 * - Full-screen centered layout
 * - Accessible design with ARIA labels
 * - Responsive design with Tailwind CSS
 */
export const TenantLoadingScreen: React.FC = () => {
  const { loading, error, refreshTenant } = useTenant();

  // Return null if not loading and no error - allows main app to render
  if (!loading && !error) {
    return null;
  }

  // Error state - show error message with retry button
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          {/* Error Icon */}
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-red-100 mb-6">
            <svg
              className="h-10 w-10 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          {/* Error Heading */}
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Failed to Load Gallery
          </h1>

          {/* Error Message */}
          <p className="text-gray-700 mb-8 leading-relaxed">
            {error}
          </p>

          {/* Retry Button */}
          <button
            onClick={refreshTenant}
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
            aria-label="Retry loading gallery configuration"
          >
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Loading state - show animated spinner
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        {/* Animated Spinner */}
        <div className="mx-auto h-16 w-16 mb-6">
          <svg
            className="animate-spin h-16 w-16 text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>

        {/* Loading Text */}
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Loading gallery...
        </h2>

        <p className="text-gray-600">
          Please wait while we prepare your experience
        </p>
      </div>
    </div>
  );
};

export default TenantLoadingScreen;
